/**
 * 版面还原预览
 *
 * 按坐标把每个文字块放回它在原页上的位置——**不做任何结构推断**。
 *
 * 【为什么要有它，以及它和「表格提取」的分工】
 * 表格提取要判断"哪几列、表头是什么"，那是一次**推断**，因此会错；
 * 而且错了往往看起来还挺像那么回事（实测一张复杂表头的表格被从中间截断，
 * 却报出置信度 100）。版面还原不做判断：块在哪就画在哪，
 * 因此**不存在"还原错了"这种失败模式**，最差也只是位置略有偏差。
 *
 * 对**扫描件**尤其有价值：原件是纯图片，没有文本层，浏览器里打开只能看，
 * 选中和复制都做不到。这里把 OCR 结果按坐标铺回去，那部分文字就变得
 * 可选中、可复制、可核对——用它来核验识别结果比读一长串流水文本直观得多。
 *
 * 【字号如何确定】
 * 用 `cqw`（容器宽度百分比单位）把块高度直接换算成字号：
 * `calc(<块高比例> * 100cqw * 0.82)`。这样不需要 JS 测量，
 * 缩放窗口、切换响应式布局都自动跟着变。
 */

import { useMemo, useState } from 'react';
import { Alert, Empty, Pagination, Space, Tag, Typography, Tooltip } from 'antd';
import { FileTextOutlined } from '@ant-design/icons';
import type { PageLayout } from '@ilc/shared';

export interface LayoutPreviewProps {
  pages: PageLayout[] | null | undefined;
}

/** 行高与字号的换算系数：块高通常是"行框高"，字号略小于它 */
const FONT_RATIO = 0.82;

export function LayoutPreview({ pages }: LayoutPreviewProps): React.ReactElement {
  const [pageNo, setPageNo] = useState(1);

  const usable = useMemo(() => (pages ?? []).filter((p) => p.blocks.length > 0), [pages]);

  if (usable.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical" size={2}>
            <span>无版面数据</span>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              该附件没有可还原的文字块（可能是解析失败、尚未提取，或原件里确实没有文字）。
            </Typography.Text>
          </Space>
        }
      />
    );
  }

  const current = usable[Math.min(pageNo, usable.length) - 1]!;
  const aspect = current.sourceHeight > 0 ? current.sourceWidth / current.sourceHeight : 0.707;

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        icon={<FileTextOutlined />}
        message="按原页坐标还原，未做任何结构推断"
        description={
          <span>
            文字位置取自原件识别结果，可选中、可复制。这一视图<strong>不重建表格结构</strong>，
            因此不存在行列错位的风险；需要结构化表格时请用上面的「提取的表格」，并以原件为准。
          </span>
        }
      />

      <Space size={12} wrap>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          第 {pageNo} / {usable.length} 页 · 源尺寸 {current.sourceWidth}×{current.sourceHeight} ·{' '}
          {current.blocks.length} 个文字块
        </Typography.Text>
        {usable.length > 1 ? (
          <Pagination
            size="small"
            current={pageNo}
            total={usable.length}
            pageSize={1}
            showSizeChanger={false}
            onChange={setPageNo}
          />
        ) : null}
      </Space>

      {/* 选用「按容器宽度缩放」：版面预览的宽度随抽屉变化，用固定像素会溢出 */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${aspect}`,
          background: '#fff',
          border: '1px solid rgba(0,0,0,0.12)',
          containerType: 'inline-size',
          overflow: 'hidden',
          userSelect: 'text',
        }}
      >
        {current.blocks.map((b, i) => (
          <Tooltip key={i} title={b.text.length > 40 ? b.text : undefined} mouseEnterDelay={0.6}>
            <span
              style={{
                position: 'absolute',
                left: `${b.x * 100}%`,
                top: `${b.y * 100}%`,
                /*
                 * 宽度按比例给，但**不强制换行**（nowrap）：OCR 恢复出的文字
                 * 与浏览器字体的字宽不可能完全一致，强制换行会把块撑高、
                 * 与下一块重叠，反而破坏版面。允许轻微溢出更接近原件观感。
                 */
                width: `${b.w * 100}%`,
                height: `${b.h * 100}%`,
                // cqw = 容器宽度的 1%，于是块高比例直接换算成字号，无需 JS 测量
                fontSize: `calc(${b.h} * 100cqw * ${FONT_RATIO})`,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                fontFamily:
                  '"Songti SC", "SimSun", "Noto Serif CJK SC", "Microsoft YaHei", serif',
                color: '#1f1f1f',
                cursor: 'text',
              }}
            >
              {b.text}
            </span>
          </Tooltip>
        ))}
      </div>

      <Space size={6} wrap>
        <Tag>共 {usable.length} 页</Tag>
        <Tag>
          总计 {usable.reduce((n, p) => n + p.blocks.length, 0)} 个文字块
        </Tag>
        {pages && pages.length !== usable.length ? (
          <Tag color="orange">{pages.length - usable.length} 页无文字块</Tag>
        ) : null}
      </Space>
    </Space>
  );
}

export default LayoutPreview;
